import {
  createArkTypeAdapter
} from "../chunk-64EJIJAJ.js";
import {
  createTypeBoxAdapter
} from "../chunk-ICBK7NC4.js";
import {
  createTypiaAdapter
} from "../chunk-7QEGHKR4.js";
import {
  createZodAdapter
} from "../chunk-XF4DWOAE.js";
import {
  lastWriteWins,
  liveEntityAt
} from "../chunk-4SQQRBMJ.js";
import {
  SubscribableImpl,
  dateNowIso,
  ensureTsPvAndFinalizeApplog,
  isInitEvent,
  mintNewEntity,
  rollingFilter
} from "../chunk-236RMLMX.js";
import "../chunk-ZAADLBSB.js";
import {
  EntityID_LENGTH
} from "../chunk-KPMINROC.js";

// src/viewmodel/builder.ts
var ObjectBuilder = class _ObjectBuilder {
  constructor(_data, en = null, _atPrefix = null, _atOverrides = {}) {
    this._data = _data;
    this.en = en;
    this._atPrefix = _atPrefix;
    this._atOverrides = _atOverrides;
  }
  _data;
  en;
  _atPrefix;
  _atOverrides;
  /**
   * Create a new builder with initial data.
   */
  static create(init = {}, en, atPrefix, atOverrides) {
    return new _ObjectBuilder(init, en, atPrefix, atOverrides);
  }
  /**
   * Update the data being built.
   */
  update(updateObj) {
    Object.assign(this._data, updateObj);
    return this;
  }
  /**
   * Build the applogs for this entity, optionally resolving against a thread.
   */
  build(thread) {
    const atPrefix = this._atPrefix;
    const applogs = [];
    for (const [key, value] of Object.entries(this._data)) {
      if (value === void 0) continue;
      const atOverride = this._atOverrides[key];
      const at = atOverride ?? (atPrefix ? `${atPrefix}/${String(key)}` : String(key));
      applogs.push({ en: this.en, at, vl: value });
    }
    return applogs;
  }
  /**
   * Get the raw data being built.
   */
  get data() {
    return { ...this._data };
  }
};

// src/viewmodel/types.ts
var GlobalVMInstances = /* @__PURE__ */ new WeakMap();
function getInstancesForThread(thread) {
  let threadMap = GlobalVMInstances.get(thread);
  if (!threadMap) {
    threadMap = /* @__PURE__ */ new Map();
    GlobalVMInstances.set(thread, threadMap);
  }
  return threadMap;
}

// src/viewmodel/factory.ts
var DefaultSignalAdapter = {
  createGetter(subscribable) {
    subscribable.subscribe(() => {
    });
    return () => subscribable.value;
  },
  createWritable(initial) {
    let current = initial;
    return [() => current, (v) => {
      current = v;
    }];
  }
};
function makeCurrent(thread, fn) {
  if (fn) return fn(thread);
  return lastWriteWins(thread, { tolerateAlreadyFiltered: true });
}
function defaultPersistApplogs(thread, applogs) {
  const finalized = applogs.map((log) => ensureTsPvAndFinalizeApplog(log, thread));
  const writable = thread;
  if (typeof writable.insertRaw === "function") {
    writable.insertRaw(finalized);
  } else if (typeof writable.insert === "function") {
    writable.insert(applogs);
  } else {
  }
}
function createViewModelFactory(options) {
  const {
    adapter,
    entityPrefix = adapter.getEntityPrefix(),
    entityIdLength = EntityID_LENGTH,
    generateEntityId,
    vmName = entityPrefix,
    signalAdapter = DefaultSignalAdapter
  } = options;
  const attrDefs = adapter.getAttributeDefs();
  const defaults = adapter.getDefaults();
  const genId = generateEntityId ?? ((data) => mintNewEntity({ ...data, ts: data.ts ?? dateNowIso() }, entityIdLength));
  const persistFn = (thread, applogs) => {
    defaultPersistApplogs(thread, applogs);
  };
  class ViewModel {
    constructor(en, thread, skipInit) {
      this.en = en;
      this.thread = thread;
      if (skipInit) return;
      const currentThread = makeCurrent(thread, options.makeCurrentThread);
      for (const attr of attrDefs) {
        if (attr.name === "en") continue;
        const attrName = attr.name;
        const atPath = attr.atPath;
        const defaultValue = attr.defaultValue ?? defaults?.[attrName];
        Object.defineProperty(this, attrName, {
          get() {
            let signal = this._signals.get(attrName);
            if (!signal) {
              const subscribable = liveEntityAt(currentThread, this.en, atPath);
              signal = this._signalAdapter.createGetter(subscribable);
              this._signals.set(attrName, signal);
            }
            const value = signal();
            return value ?? defaultValue ?? value;
          },
          set(v) {
            const applog = { en: this.en, at: atPath, vl: v };
            persistFn(this.thread, [applog]);
          },
          enumerable: true,
          configurable: true
        });
      }
      const targets = /* @__PURE__ */ new Map();
      this._targetApplogs = rollingFilter(currentThread, { vl: this.en });
      this._targetMap = new SubscribableImpl(
        targets,
        () => this._targetApplogs.subscribe((event) => {
          if (isInitEvent(event)) {
            targets.clear();
            for (const log of event.init) {
              let ats = targets.get(log.en);
              if (!ats) {
                ats = /* @__PURE__ */ new Set();
                targets.set(log.en, ats);
              }
              ats.add(log.at);
            }
          } else {
            for (const log of event.added) {
              let ats = targets.get(log.en);
              if (!ats) {
                ats = /* @__PURE__ */ new Set();
                targets.set(log.en, ats);
              }
              ats.add(log.at);
            }
            if (event.removed) {
              for (const log of event.removed) {
                const ats = targets.get(log.en);
                if (ats) {
                  ats.delete(log.at);
                  if (ats.size === 0) targets.delete(log.en);
                }
              }
            }
          }
          this._targetMap._set(targets);
        }, "derived")
      );
    }
    en;
    thread;
    /** Per-instance signal storage */
    _signals = /* @__PURE__ */ new Map();
    _signalAdapter = signalAdapter;
    /** Reactive map for applog tracking: vl === this.en */
    _targetMap;
    /** Applogs in the current thread where vl === this.en */
    _targetApplogs;
    /** Thread scoped to this entity */
    get entityThread() {
      return rollingFilter(this.thread, { en: this.en });
    }
    /** Applogs in the current thread where vl === this.en */
    get targetApplogs() {
      return this._targetApplogs;
    }
    /** Set of entities that have an applog with this.en as the vl */
    get targettedBy() {
      return new Set(this._targetMap.value.keys());
    }
    /** Set of at strings from applogs where vl === this.en */
    get targettedVia() {
      const via = /* @__PURE__ */ new Set();
      for (const ats of this._targetMap.value.values()) {
        for (const at of ats) via.add(at);
      }
      return via;
    }
    /** Whether this entity is soft-deleted */
    get isDeleted() {
      let signal = this._signals.get("__isDeleted");
      if (!signal) {
        const currentThread = makeCurrent(this.thread, options.makeCurrentThread);
        const subscribable = liveEntityAt(currentThread, this.en, "isDeleted");
        signal = this._signalAdapter.createGetter(subscribable);
        this._signals.set("__isDeleted", signal);
      }
      return !!signal();
    }
    /** Soft-delete this entity */
    setDeleted(thread = this.thread) {
      const applog = { en: this.en, at: "isDeleted", vl: true };
      persistFn(thread, [applog]);
    }
    /** Get a builder for updating this entity */
    buildUpdate(init = {}) {
      return new ObjectBuilder(init, this.en, entityPrefix);
    }
    /** Description for debugging */
    get description() {
      return `${vmName}VM(en=${this.en})`;
    }
    /** Get the full entity state as a plain object */
    toJSON() {
      const result = {};
      for (const attr of attrDefs) {
        const value = this[attr.name];
        if (value !== void 0 && value !== null) {
          result[attr.name] = value;
        }
      }
      return result;
    }
  }
  function getVMClass(en, thread) {
    if (!en || typeof en !== "string") throw new Error(`[${vmName}VM.get] invalid en: ${en}`);
    if (!thread) throw new Error(`[${vmName}VM.get] no thread provided`);
    const threadMap = getInstancesForThread(thread);
    let entityMap = threadMap.get(vmName);
    if (!entityMap) {
      entityMap = /* @__PURE__ */ new Map();
      threadMap.set(vmName, entityMap);
    }
    const existing = entityMap.get(en);
    if (existing) return existing;
    const vm = new ViewModel(en, thread, false);
    entityMap.set(en, vm);
    return vm;
  }
  function buildNewEntity(init = {}, en) {
    const entityId = en ?? genId(init);
    return ObjectBuilder.create(init, entityId, entityPrefix);
  }
  const VMClass = ViewModel;
  VMClass.get = getVMClass;
  VMClass.buildNew = buildNewEntity;
  VMClass.vmName = vmName;
  VMClass.entityPrefix = entityPrefix;
  return VMClass;
}

// src/viewmodel/schema-adapter.ts
function createAdapterFromAttributes(config) {
  return {
    getAttributeDefs: () => config.attributes,
    getDefaults: () => config.defaults ?? {},
    getEntityPrefix: () => config.entityPrefix
  };
}
function buildAtPath(entityPrefix, attrName) {
  return `${entityPrefix}/${attrName}`;
}
export {
  DefaultSignalAdapter,
  GlobalVMInstances,
  ObjectBuilder,
  buildAtPath,
  createAdapterFromAttributes,
  createArkTypeAdapter,
  createTypeBoxAdapter,
  createTypiaAdapter,
  createViewModelFactory,
  createZodAdapter,
  getInstancesForThread
};
//# sourceMappingURL=index.js.map